(function (root) {
  'use strict';
  const folded = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  // Pass only the metal option value, never the product title or diamond option.
  function normalizeMetal(value) {
    const text = folded(value).replace(/[_-]/g, ' ').replace(/\s+/g, ' ');
    if (/\b(platin\w*|pt\s?950)\b/.test(text)) return { material: 'platinum', family: 'white', purity: 'PT950' };
    const purity = text.match(/\b(9|10|14|18|22|24)\s?k(?:\s?[wyrg])?\b/)?.[1] || null;
    const candidates = [];
    if (/\b(alb|alba|white|blanc)\b/.test(text) || /\b\d{1,2}k\s?w\b/.test(text)) candidates.push('white');
    if (/\b(galben|galbena|yellow|jaune)\b/.test(text) || /\b\d{1,2}k\s?[yg]\b/.test(text)) candidates.push('yellow');
    if (/\b(roz|rose|pink)\b/.test(text) || /\b\d{1,2}k\s?r\b/.test(text)) candidates.push('rose');
    const family = candidates.length === 1 ? candidates[0] : null;
    return { material: family ? 'gold' : null, family, purity: purity ? purity + 'K' : null };
  }

  // Exact source-reviewed short galleries. Unmarked legacy galleries retain the four-view rule.
  const reviewedShortGalleries = Object.freeze({
  "ruban-de-rosee": {
    "productId": "15999497404760",
    "views": 2,
    "cadSha256": "5cbdbb4818d6c8429a3539ba630adfb8e251a5aacce108b6dfe388569d362827"
  },
  "rosace-intime": {
    "productId": "15999498879320",
    "views": 2,
    "cadSha256": "8ba157b0304fd8f8f675475a79c2fba490c5d0cc4a92dd17052cfef045bd708d"
  },
  "la-ligne-des-petits-jours": {
    "productId": "15999499698520",
    "views": 2,
    "cadSha256": "14508982f628493aa3171243399ded9f7b90d26ab2d6885cacce08cc8a7d49c2"
  },
  "les-demi-lunes-du-jour": {
    "productId": "15999506809176",
    "views": 3,
    "cadSha256": "f70abf6e56fb9907abd4197f575e497ee835a55816067fee08123481d94decb0"
  },
  "les-boucles-du-sourire": {
    "productId": "15999506841944",
    "views": 3,
    "cadSha256": "c807f16c29b451ad48a47640b18702c15c0f2fa0209821f305c52475da582ca9"
  },
  "les-petits-ponts": {
    "productId": "15999506874712",
    "views": 3,
    "cadSha256": "5c0178d0a9739ddfb530f6211ef0fcdbeddbb315a2fd21b8a4e17b04fb936fd7"
  },
  "les-accords-minuscules": {
    "productId": "15999506907480",
    "views": 3,
    "cadSha256": "35d447af8b0b42486ee5eff49d928c49db377bf09481b06717f0a820981b8ad3"
  },
  "les-petits-bastions": {
    "productId": "15999507005784",
    "views": 3,
    "cadSha256": "78a6290d8f054774f4935ac50fb56c6ea67c0b24e3404b32a01281582377835c"
  },
  "le-carre-oblique": {
    "productId": "15999507038552",
    "views": 3,
    "cadSha256": "7384645ca11f57569f73a43b998bc0120440f4018fce3dc2a1a41279ca417954"
  },
  "la-frise-des-liens": {
    "productId": "15999475515736",
    "views": 2,
    "cadSha256": "7b1144284bdf8a329c0f0b33807d02ff61bfa3ebeb8e881fa3cc37fbc1a392ce"
  }
});
  function reviewedShortGallery(product, handle) {
    const approval = reviewedShortGalleries[handle], proof = product.review_provenance;
    if (!approval || !proof || String(product.productId) !== approval.productId || proof.source_cad_sha256 !== approval.cadSha256 || proof.available_distinct_complete_views_per_colour !== approval.views || proof.mapped_images !== approval.views * 3) return false;
    const reviewed = proof.method === 'PERSONALLY_REVIEWED_CAD_AND_ALL_COLOUR_VIEWS' && proof.agent_visual_review_claimed === true;
    const audited = proof.method === 'AUTOMATED_WARM_RECOVERY_AFTER_FIRST12_REVIEWED_PILOT' && proof.agent_hero_overview_reviewed === true && proof.prior_individual_CAD_unit_and_editorial_review_reused === true && proof.automatically_verified_existing_images === approval.views * 3;
    if (!reviewed && !audited) return false;
    return ['white', 'yellow', 'rose'].every(family => {
      const group = product.groups?.[family], images = group?.images;
      return group?.complete === true && Array.isArray(images) && images.length === approval.views && images.every(image => image.approved === true && image.mediaId && image.url) && new Set(images.map(image => String(image.mediaId))).size === approval.views && new Set(images.map(image => image.url.split('?')[0])).size === approval.views;
    });
  }

  function resolve(manifest, handle, variantId) {
    if (!manifest || handle === 'epure-oval') return null;
    const product = manifest.products?.[handle];
    const id = String(variantId || '').split('/').pop();
    const binding = product?.variants?.[id];
    if (!binding) return null;
    if (product.requiresGeometryReview || product.requiresComponentReview || product.requiresSampleSpecReview) return {
      family: binding.family, material: binding.material, mode: 'reference', images: [],
      referenceImages: product.referenceImages || [], geometryOptions: binding.geometryOptions || [],
      requiresGeometryReview: product.requiresGeometryReview === true,
      requiresComponentReview: product.requiresComponentReview === true,
      requiresSampleSpecReview: product.requiresSampleSpecReview === true
    };
    const group = product.groups?.[binding.family];
    if (!group) return { family: binding.family, material: binding.material, mode: 'reference', images: [], referenceImages: product.referenceImages || [] };
    // A colour selection cannot make an unapproved recolour eligible.
    const images = (group.images || []).filter(image => image.approved === true);
    const complete = group.complete === true && (images.length >= 4 || reviewedShortGallery(product, handle));
    return {
      family: binding.family,
      material: binding.material,
      mode: complete ? 'matched-gallery' : images.length ? 'matched-hero' : 'reference',
      images,
      referenceImages: complete ? [] : product.referenceImages || [],
      sharesWhiteRepresentation: binding.material === 'platinum',
      geometryOptions: binding.geometryOptions || []
    };
  }
  root.CorsailleMetalMedia = Object.freeze({ normalizeMetal, resolve });
})(typeof globalThis !== 'undefined' ? globalThis : window);
